// OrgContext.js
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
    getOrgById,
    clearOrg,
    clearOrgUserList,
    getAllUsersByOrgId,
    getAllSitesByOrgId,
} from 'app/redux/slices/orgSlice';
import { getUserSites, clearUserSites } from 'app/redux/slices/userSlice';
import useAuth from 'app/hooks/useAuth';
import { determineHighestPrivilege } from 'app/utils';

const OrgContext = createContext(null);

export const OrgProvider = ({ orgId, children }) => {
    const dispatch = useDispatch();
    const { user } = useAuth();

    const org = useSelector(state => state.orgs.org, shallowEqual);
    const orgUsers = useSelector(state => state.orgs.orgUsersList, shallowEqual);
    const orgSites = useSelector(state => state.orgs.orgSiteList, shallowEqual);
    const userSites = useSelector(state => state.users.userSites, shallowEqual);

    const [loading, setLoading] = useState(true);
    const [rolesReady, setRolesReady] = useState(false);
    const fetchedPrivilegedData = useRef(false);

    const userId = user?.id;

    useEffect(() => {
        if (!orgId || !userId) return;


        const load = async () => {
            setLoading(true);
            try {
                await dispatch(getOrgById(orgId));
                await dispatch(getUserSites(userId));
            } catch (error) {
                console.error('Error fetching org/user sites:', error);
            } finally {
                setLoading(false);
            }
        };
        load();


        return () => {
            dispatch(clearOrg());
            dispatch(clearOrgUserList());
            dispatch(clearUserSites());
        };
    }, [orgId, userId, dispatch]);

    const userRole = user?.userRole;
    const orgRole = org?.orgRole;

    useEffect(() => {
        setRolesReady(typeof userRole === 'string' && typeof orgRole === 'string');
    }, [userRole, orgRole]);

    const highestPrivilege = useMemo(() => {
        if (!rolesReady) return null;
        return determineHighestPrivilege({ userRole, orgRole });
    }, [rolesReady, userRole, orgRole]);

    useEffect(() => {
        if (typeof highestPrivilege !== 'number') return;
        if (highestPrivilege > 1 && !fetchedPrivilegedData.current) {
            fetchedPrivilegedData.current = true;
            dispatch(getAllUsersByOrgId(orgId));
            dispatch(getAllSitesByOrgId(orgId));
        }

        return () => {
            fetchedPrivilegedData.current = false;
            dispatch(clearOrgUserList());
        };
    }, [highestPrivilege, orgId, dispatch]);

    const userOrgSites = useMemo(
        () => userSites.filter(s => s.OrgId === Number(orgId)),
        [userSites, orgId]
    );

    const contextValue = useMemo(
        () => ({ org, orgUsers, orgSites, userOrgSites, highestPrivilege, loading }),
        [org, orgUsers, orgSites, userOrgSites, highestPrivilege, loading]
    );


    return (
        <OrgContext.Provider value={{ ...contextValue }}>{children}</OrgContext.Provider>
    );
};

export const useOrg = () => {
    const context = useContext(OrgContext);
    if (!context) throw new Error('useOrg must be used within an OrgProvider');
    return context;
};



export const useOrgData = () => useOrg().org;
export const useOrgUsers = () => useOrg().orgUsers;
export const useOrgSites = () => useOrg().orgSites;
export const useUserOrgSites = () => useOrg().userOrgSites;
export const useOrgPrivilege = () => useOrg().highestPrivilege;
export const useOrgLoading = () => useOrg().loading;

